import { Container, Graphics, Sprite, Text } from 'pixi.js';
import { AssetLoader } from '../asset/AssetLoader';

const FONT_FAMILY = 'system-ui, -apple-system, sans-serif';

export interface PrimaryButtonOptions {
  label: string;
  /** Width × height of the pill. Default 200 × 56. */
  width?: number;
  height?: number;
  /** Corner radius. Defaults to half the height (perfect pill). */
  cornerRadius?: number;
  /** Fill colour at rest. */
  fillActive: number;
  /** Darker fill on hover. Omit for "no hover" buttons (e.g. bingo's BUY IN). */
  fillHover?: number;
  /** Stroke colour. Default white. */
  strokeColor?: number;
  /** Stroke alpha. Default 0.4. */
  strokeAlpha?: number;
  /** Stroke width. Default 2. */
  strokeWidth?: number;
  /** Text colour. */
  textColor: number;
  /** Text size in px. Default 22. */
  textSize?: number;
  /** Letter spacing. Default 2. */
  letterSpacing?: number;
  /** Optional asset alias for an icon Sprite rendered to the LEFT of
   * the label text — used by the slot SPIN button to anchor the
   * commissioned `icon/spin.png` next to the word "SPIN" (asset
   * brief §7). Falls back to text-only when the texture isn't
   * registered, so engine-only test scenes still read. */
  iconAlias?: string;
  /** Optional commissioned full-button image set. When the idle
   * texture is registered (and the asset bundle is loaded), the
   * widget swaps from drawn-pill + text + icon to a single Sprite
   * of the commissioned art, swapping the active texture on
   * pointer-down / disabled. Pressed and disabled fall back to
   * idle when not provided. */
  imageIdleAlias?: string;
  imagePressedAlias?: string;
  imageDisabledAlias?: string;
  /** Tap handler. */
  onTap: () => void;
}

/**
 * Generic pill-shaped primary action button with an optional hover state.
 *
 * <p>The slot SPIN button is pink-on-darker-pink with hover; the wheel
 * button is cyan-on-darker-cyan with hover; bingo's BUY IN is pink with no
 * hover. All three are this widget — only the colours, size, and label
 * differ.
 */
export class PrimaryButton extends Container {
  private readonly bg: Graphics;
  private readonly labelText: Text;
  private readonly w: number;
  private readonly h: number;
  private readonly cornerRadius: number;
  private readonly fillActive: number;
  private readonly fillHover: number | null;
  private readonly strokeColor: number;
  private readonly strokeAlpha: number;
  private readonly strokeWidth: number;
  private suppressHover = false;

  /** Image-mode sprite. When non-null the widget renders this
   * commissioned full-button texture instead of the drawn pill +
   * label + icon. Texture is swapped on pointer-down / disabled. */
  private readonly imageSprite: Sprite | null = null;
  private readonly imageIdleTex: ReturnType<
    typeof AssetLoader.shared.getTexture
  > = null;
  private readonly imagePressedTex: ReturnType<
    typeof AssetLoader.shared.getTexture
  > = null;
  private readonly imageDisabledTex: ReturnType<
    typeof AssetLoader.shared.getTexture
  > = null;
  private isPressed = false;
  private isDisabled = false;

  constructor(opts: PrimaryButtonOptions) {
    super();
    this.eventMode = 'static';
    this.cursor = 'pointer';

    this.w = opts.width ?? 200;
    this.h = opts.height ?? 56;
    this.cornerRadius = opts.cornerRadius ?? this.h / 2;
    this.fillActive = opts.fillActive;
    this.fillHover = opts.fillHover ?? null;
    this.strokeColor = opts.strokeColor ?? 0xffffff;
    this.strokeAlpha = opts.strokeAlpha ?? 0.4;
    this.strokeWidth = opts.strokeWidth ?? 2;

    // Image-mode short-circuit: if the commissioned idle texture is
    // available, render the button as a single Sprite and skip the
    // pill/label/icon rendering entirely.
    if (opts.imageIdleAlias) {
      this.imageIdleTex = AssetLoader.shared.getTexture(opts.imageIdleAlias);
      this.imagePressedTex = opts.imagePressedAlias
        ? AssetLoader.shared.getTexture(opts.imagePressedAlias)
        : null;
      this.imageDisabledTex = opts.imageDisabledAlias
        ? AssetLoader.shared.getTexture(opts.imageDisabledAlias)
        : null;
    }

    this.bg = new Graphics();
    this.labelText = new Text({
      text: opts.label,
      style: {
        fontFamily: FONT_FAMILY,
        fontSize: opts.textSize ?? 22,
        fontWeight: '800',
        fill: opts.textColor,
        letterSpacing: opts.letterSpacing ?? 2,
      },
    });
    this.labelText.anchor.set(0.5);

    if (this.imageIdleTex) {
      // Render the commissioned button image only.
      const sprite = new Sprite(this.imageIdleTex);
      sprite.anchor.set(0.5);
      const aspect = this.imageIdleTex.width / this.imageIdleTex.height;
      // Fit to height; width follows aspect (asset is roughly square
      // at 1:1, but we don't assume).
      sprite.height = this.h;
      sprite.width = this.h * aspect;
      this.imageSprite = sprite;
      this.addChild(sprite);
    } else {
      this.addChild(this.bg);
      this.draw(true);
      this.addChild(this.labelText);

      // Optional icon sprite to the left of the label text. Sized to
      // ~75% of the button's interior height so it sits comfortably
      // inside the pill without crowding the text.
      if (opts.iconAlias) {
        const tex = AssetLoader.shared.getTexture(opts.iconAlias);
        if (tex) {
          const iconSize = Math.floor(this.h * 0.55);
          const sprite = new Sprite(tex);
          sprite.anchor.set(0.5);
          const aspect = tex.width / tex.height;
          sprite.height = iconSize;
          sprite.width = iconSize * aspect;
          // Reserve 6px gap between sprite (right edge) and label
          // (left edge). Compute label/sprite widths and offset both
          // around the button's centre so the pair is itself centred.
          const gap = 6;
          const total = sprite.width + gap + this.labelText.width;
          sprite.x = -total / 2 + sprite.width / 2;
          this.labelText.x = total / 2 - this.labelText.width / 2;
          this.addChild(sprite);
        }
      }
    }

    this.on('pointertap', () => {
      if (!this.isDisabled) opts.onTap();
    });
    if (this.imageSprite) {
      this.on('pointerdown', () => this.setPressed(true));
      this.on('pointerup', () => this.setPressed(false));
      this.on('pointerupoutside', () => this.setPressed(false));
    } else if (this.fillHover !== null) {
      this.on('pointerover', () => {
        if (!this.suppressHover) this.draw(false);
      });
      this.on('pointerout', () => this.draw(true));
    }
  }

  setLabel(text: string): void {
    if (this.imageSprite) return; // image-mode buttons have baked labels
    this.labelText.text = text;
  }

  /** Suppress the hover-darken effect — useful while the button is
   * functionally inert (e.g. mid-spin). */
  setSuppressHover(suppress: boolean): void {
    this.suppressHover = suppress;
    if (!this.imageSprite && suppress) this.draw(true);
  }

  /** Disable / re-enable the button. In image mode swaps to the
   * disabled texture if provided; in pill mode just dims via alpha
   * (existing semantics). */
  setDisabled(disabled: boolean): void {
    this.isDisabled = disabled;
    this.cursor = disabled ? 'default' : 'pointer';
    if (this.imageSprite) {
      this.imageSprite.texture = this.currentTex();
    } else {
      this.alpha = disabled ? 0.5 : 1;
    }
  }

  private setPressed(pressed: boolean): void {
    if (this.isPressed === pressed) return;
    this.isPressed = pressed;
    if (this.imageSprite) {
      this.imageSprite.texture = this.currentTex();
    }
  }

  private currentTex(): import('pixi.js').Texture {
    if (this.isDisabled && this.imageDisabledTex) return this.imageDisabledTex;
    if (this.isPressed && this.imagePressedTex) return this.imagePressedTex;
    return this.imageIdleTex!;
  }

  private draw(active: boolean): void {
    this.bg.clear();
    const fill = active || this.fillHover === null ? this.fillActive : this.fillHover;
    this.bg
      .roundRect(-this.w / 2, -this.h / 2, this.w, this.h, this.cornerRadius)
      .fill({ color: fill, alpha: 0.95 })
      .stroke({ color: this.strokeColor, alpha: this.strokeAlpha, width: this.strokeWidth });
  }
}
